<template>
  <div class="project-modal" :class="{ showing: project_id }">
    <div class="modal-body">
      <i v-show="!saving" class="material-icons close-btn" @click="close(true)"
      >clear</i
      >

      <div class="top-panel">
        <h4 v-show="!saving">
          {{ project_id === -1 ? 'New' : 'Edit' }} Project
        </h4>
        <h4 class="saving-header" v-show="saving">Saving Changes</h4>
        <tab-bar
          :tabs="tabs"
          :tabIndex="tabIndex"
          :onClick="changeTab"
          :class="{ hidden: saving }"
        />
      </div>

      <div v-show="!saving" class="middle-panel" ref="scrollzone">
        <projectmodal-details
          :project="project"
          :class="{ visible: project_id && tabIndex === 0 }"
        />
        <projectmodal-team
          :project="project"
          :class="{ visible: project_id && tabIndex === 1 }"
        />
        <projectmodal-type
          :project="project"
          :class="{ visible: project_id && tabIndex === 2 }"
        />
        <projectmodal-benefits
          :project="project"
          :class="{ visible: project_id && tabIndex === 3 }"
          class="wide"
          @forceRefresh="forceRefreshFlag = true"
        />
        <projectmodal-documents
          :project="project"
          :class="{ visible: project_id && tabIndex === 4 }"
          class="wide docs"
          :onChange="docsChanged"
        />
      </div>

      <div v-show="saving" class="middle-panel saving">
        <img src="~@/assets/icons/spinner.svg"/>
        <p>Saving</p>
      </div>

      <div class="bottom-panel" v-show="!saving">
        <a
          class="button delete-button"
          v-if="project_id && project_id !== -1"
          @click="deleteProject()"
        >Delete</a
        >
        <a class="button secondary-button" @click="close(true)">Cancel</a>
        <a
          class="button"
          @click="save()"
          :disabled="!project.name || !project.currencyId || !project.categoryId"
        >Save</a
        >
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import TabBar from './TabBar.vue';
import ProjectModal_Details from './ProjectModal_Details';
import ProjectModal_Team from './ProjectModal_Team';
import ProjectModal_Type from './ProjectModal_Type';
import ProjectModal_Benefits from './ProjectModal_Benefits';
import ProjectModal_Documents from './ProjectModal_Documents';

export default {
  name: 'ProjectModal',
  data() {
    return {
      project: this.$store.getters.project_schema,
      tabIndex: 0,
      saving: false,
      docs_edited: false,
      forceRefreshFlag: false,
      tabs: [
        {title: 'Details', active: this.tabIndex, icon: '/icons/info.svg'},
        {title: 'Team', icon: '/icons/person.svg'},
        {title: 'Type', icon: '/icons/champion.svg'},
        {title: 'Benefits', icon: '/icons/benefit.svg'},
        {title: 'Documents', icon: '/icons/document.svg'},
      ],
    };
  },
  components: {
    'tab-bar': TabBar,
    'projectmodal-details': ProjectModal_Details,
    'projectmodal-team': ProjectModal_Team,
    'projectmodal-type': ProjectModal_Type,
    'projectmodal-benefits': ProjectModal_Benefits,
    'projectmodal-documents': ProjectModal_Documents,
  },
  methods: {
    async close(checkForSave) {
      // if they've made edits, and have clicked the close icon or cancel button
      if (checkForSave && this.form_dirty) {
        try {
          const options = {
            html: false,
            okText: 'Continue',
            cancelText: 'Cancel',
            customClass: 'acada-confirm',
            animation: 'fade',
          };

          const message = {
            title: 'Unsaved changes',
            body:
              'You have unsaved changes - are you sure you want to close this window?',
          };

          await this.$dialog.confirm(message, options);

          this.closeModal();
        } catch (e) {
          // they clicked cancel, so don't close the modal
        }
      } else {
        this.closeModal();
      }
    },
    async closeModal() {
      if (this.forceRefreshFlag && this.project.id) {
        this.forceRefreshFlag = false;
        await this.$store.dispatch('getProjectDetail', this.project.id);
      }
      this.$store.dispatch('setDocsForEdit', []);
      this.$store.dispatch('setProjectModalID', null);
    },
    async promptMarkActive() {
      try {
        const options = {
          html: false,
          okText: 'Continue',
          cancelText: 'Cancel',
          customClass: 'acada-confirm',
          animation: 'fade',
        };

        const message = {
          title: 'Project Completed',
          body: `You've marked this project as Completed, but you still have tasks due. Do you want to continue?`,
        };

        return this.$dialog.confirm(message, options);
      } catch (e) {
        console.log(e);
        // they clicked cancel, so don't close the modal
      }
    },
    async save() {
      try {
        if (!(await this.checkProjectValid())) return;
        if (
          this.project_id !== -1 &&
          this.project.status === 'Completed' &&
          this.completion < 100
        ) {
          await this.promptMarkActive();
        }

        this.saving = true;
        if (this.project_id === -1) {
          this.project.createdById = this.app_user.id;
          this.project.organizationId = this.app_user.organizationId;
          delete this.project.relatedProjectsIds;
        }

        const saved_project_id = await this.$store.dispatch(
          'saveProject',
          this.project
        );

        let id = this.project.id || null;
        if (id === -1 || id === null || typeof id === 'undefined') {
          id = saved_project_id;
        }

        await this.$store.dispatch('saveDocsForEdit', id);
        await this.$store.dispatch('getProjectDetail', id);
        this.forceRefreshFlag = false;

        if (this.project_id === -1) {
          const new_project_id = Object.keys(this.$store.getters.projects).find(
            key => {
              if (this.$store.getters.projects[key].id === id) {
                return true;
              }
              return false;
            }
          );

          this.$router.push({
            name: 'ProjectDetail',
            params: {id: new_project_id},
          });
        }

        this.close(false);
      } catch (e) {
        const options = {
          html: false,
          okText: 'Cancel',
          customClass: 'acada-confirm',
          animation: 'fade',
        };

        const message = {
          title: `Error saving project`,
          body: 'An error occurred when trying to save the project',
        };

        await this.$dialog.alert(message, options);
        console.error(e);
      } finally {
        this.saving = false;
      }
    },
    changeTab(index) {
      this.$store.dispatch(
        'setProjectModalView',
        this.tabs[index].title.toLowerCase()
      );
      this.tabIndex = index;
    },
    reset() {
      this.saving = false;
      this.docs_edited = false;
      this.project = this.$store.getters.project_schema;
    },
    toDate(str) {
      if (!str) {
        return null;
      }
      const date = moment(str, 'YYYY-MM-DDTHH:mm:ss:SSSZ').toDate();
      return date;
    },
    docsChanged() {
      this.docs_edited = true;
    },
    async deleteProject() {
      try {
        const options = {
          html: false,
          okText: 'Delete',
          cancelText: 'Cancel',
          customClass: 'acada-confirm',
          animation: 'fade',
        };

        const message = {
          title: 'Delete project',
          body:
            'Are you sure you want to delete this project? This action cannot be undone',
        };

        await this.$dialog.confirm(message, options);
        await this.$store.dispatch('deleteProject', this.project.id);
        this.$store.dispatch('setDocsForEdit', []);
        this.$store.dispatch('setProjectModalID', null);
        this.$router.push({name: 'Projects'});
      } catch (e) {
        // they clicked cancel, so don't close the modal
      }
    },
    async checkProjectValid() {
      let errorMessage;

      // Validate benefits
      let benefitsError;
      if (this.project.projectBenefits) {
        for (const b of this.project.projectBenefits) {
          if (
            !(
              ((!b.forecastedAmount && !b.forecastedDate) ||
                (!!b.forecastedAmount && !!b.forecastedDate)) &&
              ((!b.amount && !b.realizedDate) ||
                (!!b.amount && !!b.realizedDate))
            )
          ) {
            benefitsError =
              'Benefits must either have a forecasted amount with a forecasted date or a realized amount with a realized date';
          } else if (!b.savingsTypeId) {
            benefitsError = 'Benefits must have a savings type';
          } else if (!b.yearEnding) {
            benefitsError = 'Benefits must have a financial year specified';
          }
        }
      }

      if (!this.project.name) {
        errorMessage = 'You need to specify a name for the project';
      } else if (!this.project.currencyId) {
        errorMessage = 'You need to specify a currency for the project';
      } else if (!this.project.categoryId) {
        errorMessage = 'You need to specify a team for the project';
      } else if (benefitsError) {
        errorMessage = benefitsError;
      }
      if (errorMessage) {
        const options = {
          html: false,
          okText: 'Cancel',
          customClass: 'acada-confirm',
          animation: 'fade',
        };

        const message = {
          title: `Can't save project`,
          body: errorMessage,
        };

        await this.$dialog.alert(message, options);
        return false;
      }
      return true;
    },
  },
  computed: {
    project_id() {
      return this.$store.getters.project_modal_id;
    },
    project_view() {
      return this.$store.getters.project_modal_view;
    },
    projects() {
      return this.$store.getters.projects;
    },
    app_user() {
      return this.$store.getters.app_user;
    },
    unsaved_project_string() {
      let projectString = '';

      // existing project
      if (this.project_id !== -1 && this.projects[this.project_id]) {
        projectString = JSON.stringify(this.projects[this.project_id]);
      } else {
        projectString = JSON.stringify(this.$store.getters.project_schema);
      }

      return projectString;
    },
    edited_project_string() {
      if (!this.project) return '';
      return JSON.stringify(this.project);
    },
    form_dirty() {
      if (
        this.unsaved_project_string &&
        this.edited_project_string &&
        this.unsaved_project_string !== this.edited_project_string
      ) {
        return true;
      }

      if (this.docs_edited) {
        return true;
      }

      return false;
    },
    start_date() {
      return this.project.startDate;
    },
    action_groups() {
      // TODO - change the default param below
      let groups = this.$store.getters.actions || [];
      groups.forEach(group => {
        group.actions = group.actions.sort(this.sortByDate);
      });
      return groups;
    },
    all_actions() {
      let all = [];
      this.action_groups.forEach(group => {
        all = [...all, ...group.actions];
      });
      return all;
    },
    completed() {
      if (!this.all_actions || !this.all_actions.length) {
        return 0;
      }

      let count = 0;

      this.all_actions.forEach(action => {
        if (action.completed) {
          count++;
        }
      });

      return count;
    },
    completion() {
      if (!this.all_actions || !this.all_actions.length) {
        // if the project is marked as completed, we can return 100%, else it's automatically 0
        return this.project.status === 'Completed' ? 100 : 0;
      }
      return Math.round((100 * this.completed) / this.all_actions.length);
    },
  },
  watch: {
    project_id() {
      this.reset();

      if (this.project_id) {
        const clone = JSON.parse(this.unsaved_project_string);

        // rehydrate dates
        Object.keys(clone).forEach(key => {
          if (
            (key.toLowerCase().includes('date') && key !== 'projectUpdates') ||
            key === 'negPlanCompleted'
          ) {
            clone[key] = this.toDate(clone[key]);
          }
        });

        this.project = clone;
      }
    },
    project_view(newValue) {
      switch (newValue) {
        case 'team':
          this.tabIndex = 1;
          break;
        case 'type':
          this.tabIndex = 2;
          break;
        case 'benefits':
          this.tabIndex = 3;
          break;
        case 'documents':
          this.tabIndex = 4;
          break;
        case 'details':
        default:
          this.tabIndex = 0;
      }
    },
    tabIndex() {
      this.$refs.scrollzone.scrollTo(0, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/_styles.scss';

.project-modal {
  opacity: 0;
  visibility: hidden;
  left: -10000px;
  position: absolute;
  z-index: 2000;
  top: 0;
  width: 0;
  height: 0;
  transition: opacity 0.5s;

  .modal-body {
    width: calc(100% - 100px);
    max-width: 740px;
    height: calc(100% - 70px);
    position: fixed;
    z-index: 20;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(calc(-50% - 100px)) translateZ(0);
    background: white;
    will-change: transform;
    transition: transform 500ms;
    box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: 0;

    .close-btn {
      position: absolute;
      top: 0px;
      right: 0px;
      padding: 10px;
      color: #acb6c1;
      cursor: pointer;
      user-select: none;
      font-size: 20px;
    }

    .top-panel {
      width: 100%;
      height: 126px;
      border-bottom: 1px solid #e2e5ec;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-direction: column;

      h4 {
        font-size: 24px;
        margin-top: 35px;
      }

      h4.saving-header {
        margin-top: 60px;
      }

      .hidden {
        pointer-events: none;
        visibility: hidden;
      }
    }

    .middle-panel {
      width: 100%;
      flex: 1;
      overflow-x: hidden;
      overflow-y: auto;
      background: $bg-color;
      padding: 30px;
      opacity: 0;
      transition: opacity 0.1s;
      position: relative;

      > div {
        max-width: 460px;
        display: block;
        margin: 0 auto;
        position: absolute;
        top: 0;
        left: -99999px;

        &.visible {
          position: relative;
          left: auto;
        }

        &.wide {
          margin: 0 40px 0 15px;
          max-width: none;

          h6:first-child {
            margin-top: 20px;
          }

          &.docs {
            margin-right: 15px;
          }
        }
      }

      ::v-deep h6 {
        margin-top: 10px;
        margin-bottom: 20px;

        &.spaced {
          margin-top: 40px;
        }
      }

      &.saving {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        flex-wrap: nowrap;

        img {
          width: 40px;
          height: 40px;
          animation: spin 1.5s linear infinite;
        }

        p {
          font-weight: 400;
          font-size: 14px;
          color: #565b64;
          margin: 20px 0 0;
        }
      }
    }

    .bottom-panel {
      width: 100%;
      height: 90px;
      border-top: 1px solid #e2e5ec;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-direction: row;
      padding: 0 40px;

      .button {
        width: 130px;
        min-width: 130px;
        margin: 0;
        margin-left: 10px;

        &[disabled] {
          pointer-events: none;
          opacity: 0.5;
        }
      }
    }
  }

  &.showing {
    background: rgba(38, 41, 47, 0.6);
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 1;
    visibility: visible;

    .modal-body {
      transform: translateX(-50%) translateY(-50%) translateZ(0);
    }

    .middle-panel {
      opacity: 1;
    }
  }
}

::v-deep .small-caps {
  text-transform: uppercase;
  font-size: 10px;
  color: #929da9;
  font-weight: 500;
  margin-top: 20px;

  &:first-child {
    margin-top: 0;
  }
}

::v-deep .grid {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: flex-start;

  > .datepicker {
    width: 215px;
    margin-bottom: 30px;
    margin-top: 0px;

    label {
      text-transform: uppercase;
      font-size: 10px;
      display: block;
      font-weight: 500;
      color: #929da9;
    }

    &.dense {
      margin-bottom: 16px;
    }
  }
}

@keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>
